/**
 * Content Intent Detection and Analysis
 * Визначає тип контенту, інтент користувача та структуру статті
 */

export type ContentIntent = 
  | 'commercial' // Комерційні запити (купити, ціна, замовити)
  | 'informational' // Інформаційні (як, що таке, чому)
  | 'navigational' // Навігаційні (бренд, назва сайту)
  | 'transactional'; // Транзакційні (скачати, зареєструватися)

export type ContentType = 
  | 'category' // Категорія магазину
  | 'product' // Картка товару
  | 'blog-how-to' // How-to стаття
  | 'blog-best' // Best/топ/порівняння
  | 'blog-review' // Огляд
  | 'blog-guide' // Гайд/посібник
  | 'blog-news' // Новина
  | 'landing' // Лендінг
  | 'service' // Сторінка послуги
  | 'general'; // Загальна стаття

export interface ContentAnalysis {
  intent: ContentIntent;
  contentType: ContentType;
  confidence: number; // 0-1
  indicators: {
    commercialSignals: string[];
    informationalSignals: string[];
    structuralPatterns: string[];
  };
  language: string;
  competitorPatterns: {
    dominantType: ContentType;
    hasEcommerce: boolean;
    hasBlog: boolean;
    avgHeaderCount: number;
    commonStructure: string[];
  };
}

// Комерційні індикатори
const COMMERCIAL_KEYWORDS = [
  // Українська
  'купити', 'ціна', 'вартість', 'замовити', 'доставка', 'оплата', 
  'знижка', 'акція', 'каталог', 'магазин', 'товар', 'продаж',
  'прайс', 'вартість', 'кошторис', 'придбати',
  // Російська
  'купить', 'цена', 'стоимость', 'заказать', 'доставка', 'оплата',
  'скидка', 'акция', 'каталог', 'магазин', 'товар', 'продажа',
  // Англійська
  'buy', 'price', 'cost', 'order', 'delivery', 'payment',
  'discount', 'sale', 'shop', 'store', 'product', 'purchase'
];

// Інформаційні індикатори
const INFORMATIONAL_KEYWORDS = [
  // Українська
  'як', 'що таке', 'чому', 'коли', 'де', 'який', 'яка',
  'інструкція', 'гайд', 'порада', 'рекомендації', 'огляд',
  'топ', 'кращі', 'найкращі', 'список', 'порівняння',
  // Російська
  'как', 'что такое', 'почему', 'когда', 'где', 'какой', 'какая',
  'инструкция', 'гайд', 'совет', 'рекомендации', 'обзор',
  'топ', 'лучшие', 'список', 'сравнение',
  // Англійська
  'how', 'what', 'why', 'when', 'where', 'which',
  'guide', 'tutorial', 'tips', 'recommendations', 'review',
  'top', 'best', 'list', 'comparison'
];

// Транзакційні індикатори
const TRANSACTIONAL_KEYWORDS = [
  'скачати', 'завантажити', 'зареєструватися', 'підписатися',
  'скачать', 'загрузить', 'зарегистрироваться', 'подписаться',
  'download', 'register', 'subscribe', 'sign up'
];

/**
 * Визначає інтент на основі ключового слова
 */
export function detectKeywordIntent(keyword: string): ContentIntent {
  const lowerKeyword = keyword.toLowerCase();
  
  // Перевіряємо комерційні сигнали
  const commercialScore = COMMERCIAL_KEYWORDS.filter(k => 
    lowerKeyword.includes(k)
  ).length;
  
  // Перевіряємо інформаційні сигнали
  const informationalScore = INFORMATIONAL_KEYWORDS.filter(k => 
    lowerKeyword.includes(k)
  ).length;
  
  // Перевіряємо транзакційні сигнали
  const transactionalScore = TRANSACTIONAL_KEYWORDS.filter(k => 
    lowerKeyword.includes(k)
  ).length;
  
  // Визначаємо основний інтент
  if (transactionalScore > 0) return 'transactional';
  if (commercialScore > informationalScore) return 'commercial';
  if (informationalScore > 0) return 'informational';
  
  // Якщо це просто бренд або назва
  const words = lowerKeyword.split(' ').filter(w => w.length > 2);
  if (words.length <= 2) return 'navigational';
  
  return 'informational'; // За замовчуванням
}

/**
 * Визначає тип контенту на основі ключового слова
 */
export function detectContentType(keyword: string, intent: ContentIntent): ContentType {
  const lowerKeyword = keyword.toLowerCase();
  
  // Best/Топ статті
  if (lowerKeyword.match(/топ|top|кращі|лучшие|best|найкращі/)) {
    return 'blog-best';
  }
  
  // How-to статті
  if (lowerKeyword.match(/як|как|how to|інструкція|инструкция/)) {
    return 'blog-how-to';
  }
  
  // Огляди
  if (lowerKeyword.match(/огляд|обзор|review|рецензія/)) {
    return 'blog-review';
  }
  
  // Гайди
  if (lowerKeyword.match(/гайд|гид|guide|посібник|пособие/)) {
    return 'blog-guide';
  }
  
  // Комерційні типи
  if (intent === 'commercial') {
    // Категорія магазину
    if (lowerKeyword.match(/купити|купить|buy|каталог|catalog/) && 
        lowerKeyword.split(' ').length <= 3) {
      return 'category';
    }
    
    // Конкретний товар
    if (lowerKeyword.match(/ціна|цена|price|вартість|стоимость/)) {
      return 'product';
    }
    
    return 'category'; // За замовчуванням для комерційних
  }
  
  return 'general';
}

/**
 * Аналізує структуру конкурентів з SERP
 */
export function analyzeCompetitorPatterns(serpResults: any[]): {
  dominantType: ContentType;
  hasEcommerce: boolean;
  hasBlog: boolean;
  avgHeaderCount: number;
  commonStructure: string[];
} {
  if (!serpResults || serpResults.length === 0) {
    return {
      dominantType: 'general',
      hasEcommerce: false,
      hasBlog: false,
      avgHeaderCount: 0,
      commonStructure: []
    };
  }
  
  let ecommerceCount = 0;
  let blogCount = 0;
  let totalHeaders = 0;
  const urlPatterns: Record<string, number> = {};
  
  serpResults.forEach(result => {
    const url = result.url?.toLowerCase() || '';
    const title = result.title?.toLowerCase() || '';
    const description = result.description?.toLowerCase() || '';
    
    // Перевіряємо e-commerce сигнали
    if (url.includes('/shop') || url.includes('/product') || 
        url.includes('/catalog') || url.includes('/category') ||
        title.includes('купити') || title.includes('купить') || title.includes('buy')) {
      ecommerceCount++;
    }
    
    // Перевіряємо blog сигнали
    if (url.includes('/blog') || url.includes('/article') ||
        title.match(/як|как|how|топ|top|кращі|best/)) {
      blogCount++;
    }
    
    // Аналізуємо структуру URL
    const pathParts = url.split('/').filter(p => p && p.length > 0);
    pathParts.forEach(part => {
      if (part.length > 2 && !part.includes('?')) {
        urlPatterns[part] = (urlPatterns[part] || 0) + 1;
      }
    });
  });
  
  // Визначаємо домінуючий тип
  let dominantType: ContentType = 'general';
  if (ecommerceCount > serpResults.length / 2) {
    dominantType = 'category';
  } else if (blogCount > serpResults.length / 2) {
    dominantType = 'blog-guide';
  }
  
  // Знаходимо загальні структурні елементи
  const commonStructure = Object.entries(urlPatterns)
    .filter(([_, count]) => count >= serpResults.length * 0.3) // 30%+ присутність
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([pattern]) => pattern);
  
  return {
    dominantType,
    hasEcommerce: ecommerceCount > 0,
    hasBlog: blogCount > 0,
    avgHeaderCount: serpResults.length > 0 ? totalHeaders / serpResults.length : 0,
    commonStructure
  };
}

/**
 * Головна функція: повний аналіз контенту
 */
export function analyzeContent(params: {
  keyword: string;
  language: string;
  serpResults?: any[];
  keywordData?: any;
}): ContentAnalysis {
  const { keyword, language, serpResults = [], keywordData } = params;
  
  // Визначаємо інтент
  const intent = detectKeywordIntent(keyword);
  
  // Визначаємо тип контенту
  const contentType = detectContentType(keyword, intent);
  
  // Аналізуємо конкурентів
  const competitorPatterns = analyzeCompetitorPatterns(serpResults);
  
  // Збираємо індикатори
  const commercialSignals: string[] = [];
  const informationalSignals: string[] = [];
  const structuralPatterns: string[] = [];
  
  COMMERCIAL_KEYWORDS.forEach(k => {
    if (keyword.toLowerCase().includes(k)) {
      commercialSignals.push(k);
    }
  });
  
  INFORMATIONAL_KEYWORDS.forEach(k => {
    if (keyword.toLowerCase().includes(k)) {
      informationalSignals.push(k);
    }
  });
  
  if (competitorPatterns.hasEcommerce) {
    structuralPatterns.push('E-commerce presence in SERP');
  }
  if (competitorPatterns.hasBlog) {
    structuralPatterns.push('Blog content in SERP');
  }
  if (competitorPatterns.commonStructure.length > 0) {
    structuralPatterns.push(`Common URL patterns: ${competitorPatterns.commonStructure.join(', ')}`);
  }
  
  // Розраховуємо впевненість
  const totalSignals = commercialSignals.length + informationalSignals.length + structuralPatterns.length;
  const confidence = totalSignals > 0 ? Math.min(totalSignals / 10, 1) : 0.5;
  
  return {
    intent,
    contentType,
    confidence,
    indicators: {
      commercialSignals,
      informationalSignals,
      structuralPatterns
    },
    language,
    competitorPatterns
  };
}

/**
 * Генерує рекомендації по структурі на основі аналізу
 */
export function getStructureRecommendations(analysis: ContentAnalysis): {
  recommendedSections: string[];
  priorityElements: string[];
  contentGuidelines: string[];
} {
  const recommendations = {
    recommendedSections: [] as string[],
    priorityElements: [] as string[],
    contentGuidelines: [] as string[]
  };
  
  // Рекомендації по типу контенту
  switch (analysis.contentType) {
    case 'category':
      recommendations.recommendedSections = [
        'Вступ з описом категорії',
        'Фільтри та сортування товарів',
        'Сітка товарів з короткими описами',
        'FAQ по категорії',
        'SEO текст внизу сторінки'
      ];
      recommendations.priorityElements = [
        'Хлібні крихти (breadcrumbs)',
        'H1 з ключовим словом',
        'Structured data (Product/Offer)',
        'Внутрішнє посилання на товари'
      ];
      recommendations.contentGuidelines = [
        'Фокус на комерційних сигналах',
        'Чіткі CTA (Call To Action)',
        'Ціни та наявність',
        'Переваги покупки'
      ];
      break;
      
    case 'blog-best':
      recommendations.recommendedSections = [
        'Вступ з контекстом топу',
        'Методологія вибору',
        'Numbered list з детальними описами',
        'Порівняльна таблиця',
        'Висновки та рекомендації',
        'FAQ'
      ];
      recommendations.priorityElements = [
        'H1 з "Топ", "Кращі", "Best"',
        'H2 для кожного пункту списку',
        'Зображення продуктів/сервісів',
        'Таблиця порівняння характеристик'
      ];
      recommendations.contentGuidelines = [
        'Об\'єктивні критерії оцінки',
        'Переваги та недоліки кожного варіанту',
        'Конкретні цифри та факти',
        'Особиста рекомендація експерта'
      ];
      break;
      
    case 'blog-how-to':
      recommendations.recommendedSections = [
        'Вступ (проблема та її рішення)',
        'Що вам знадобиться',
        'Покрокова інструкція',
        'Поради та лайфхаки',
        'Висновок',
        'FAQ'
      ];
      recommendations.priorityElements = [
        'H1 з "Як", "How to"',
        'Нумерований список кроків',
        'Зображення/скріншоти для кожного кроку',
        'Блоки з порадами (tip boxes)'
      ];
      recommendations.contentGuidelines = [
        'Чіткі, зрозумілі інструкції',
        'Логічна послідовність кроків',
        'Візуальні приклади',
        'Можливі проблеми та їх рішення'
      ];
      break;
      
    case 'blog-guide':
      recommendations.recommendedSections = [
        'Вступ (що охоплює гайд)',
        'Зміст (table of contents)',
        'Розділи по темах (3-7 секцій)',
        'Практичні приклади',
        'Чек-лист або підсумок',
        'FAQ'
      ];
      recommendations.priorityElements = [
        'H1 з "Гайд", "Посібник", "Guide"',
        'Якірні посилання до секцій',
        'H2/H3 ієрархія заголовків',
        'Виділення ключових моментів'
      ];
      recommendations.contentGuidelines = [
        'Всеосяжність теми',
        'Структурована подача інформації',
        'Від простого до складного',
        'Додаткові ресурси та посилання'
      ];
      break;
      
    case 'blog-review':
      recommendations.recommendedSections = [
        'Вступ (що оглядається)',
        'Огляд характеристик',
        'Тестування/досвід використання',
        'Переваги',
        'Недоліки',
        'Висновок та оцінка',
        'FAQ'
      ];
      recommendations.priorityElements = [
        'H1 з назвою продукту/сервісу',
        'Рейтинг або оцінка',
        'Зображення високої якості',
        'Порівняння з альтернативами'
      ];
      recommendations.contentGuidelines = [
        'Об\'єктивність та чесність',
        'Особистий досвід використання',
        'Конкретні приклади та факти',
        'Для кого підходить/не підходить'
      ];
      break;
      
    default:
      recommendations.recommendedSections = [
        'Вступ',
        'Основна частина (2-4 секції)',
        'Висновок',
        'FAQ (опціонально)'
      ];
      recommendations.priorityElements = [
        'H1 з основним ключовим словом',
        'H2 для основних секцій',
        'Логічна структура заголовків',
        'Внутрішні посилання'
      ];
      recommendations.contentGuidelines = [
        'Відповідь на пошуковий інтент',
        'Структурована подача інформації',
        'Читабельність та зрозумілість',
        'Унікальна цінність контенту'
      ];
  }
  
  // Додаткові рекомендації по інтенту
  if (analysis.intent === 'commercial') {
    recommendations.priorityElements.push('Переваги покупки', 'CTA кнопки');
  }
  
  return recommendations;
}
